import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

/* ---------------- YouTube Embed ---------------- */
const processYouTubeLinks = (content = "") => {
  const youtubeRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[&?][\w=&]*)?/g;

  return content.replace(youtubeRegex, (_, videoId) => {
    return `
<div class="youtube-embed-wrapper">
  <iframe
    src="https://www.youtube.com/embed/${videoId}"
    frameborder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowfullscreen
  ></iframe>
</div>`;
  });
};

/* ---------------- Copy Button ---------------- */
const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button onClick={handleCopy} className="copy-code-button" title="Copy code">
      {copied ? "Copied ✓" : "Copy"}
    </button>
  );
};

/* ---------------- Renderer ---------------- */
const MarkdownRenderer = ({ content = "", className = "" }) => {
  const processedContent = processYouTubeLinks(content);

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          /* -------- HEADINGS (FIXES ## ISSUE) -------- */
          h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
          h2: ({ children }) => <h2 className="md-h2">{children}</h2>,
          h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
          h4: ({ children }) => <h4 className="md-h4">{children}</h4>,

          /* -------- PARAGRAPH & LINE BREAKS -------- */
          p: ({ children }) => <p className="md-p">{children}</p>,
          br: () => <br />,

          /* -------- LINKS -------- */
          a: ({ href, children }) => (
            <a
              href={href}
              className="markdown-link"
              target={href?.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),

          /* -------- IMAGES -------- */
          img: ({ alt, src, ...props }) => (
            <img
              {...props}
              src={src}
              alt={alt || "Blog post image"}
              className="markdown-image"
              loading="lazy"
              decoding="async"
            />
          ),

          /* -------- CODE -------- */
          code: ({ inline, className, children }) => {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "text";
            const codeString = String(children).replace(/\n$/, "");

            if (inline) {
              return <code className="inline-code">{children}</code>;
            }

            return (
              <div className="code-block-wrapper">
                <div className="code-block-header">
                  <span className="code-language">{language}</span>
                  <CopyButton code={codeString} />
                </div>
                <SyntaxHighlighter
                  language={language}
                  style={oneLight}
                  PreTag="div"
                  customStyle={{
                    margin: 0,
                    background: "#F1EEE5",
                    padding: "1.25rem",
                    fontSize: "0.9rem",
                    lineHeight: "1.7",
                  }}
                >
                  {codeString}
                </SyntaxHighlighter>
              </div>
            );
          },

          /* -------- BLOCKQUOTE -------- */
          blockquote: ({ children }) => (
            <blockquote className="markdown-blockquote">
              💡 {children}
            </blockquote>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>

      {/* ---------- STYLES ---------- */}
      <style>{`
        .markdown-content {
          color: #3D4B59;
          line-height: 1.9;
          font-size: 1.05rem;
        }

        .md-h1 {
          font-size: 2.75rem;
          font-weight: 900;
          margin: 3rem 0 2rem;
          background: linear-gradient(135deg,#A8720E,#E8B44C);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .md-h2 {
          font-size: 2.1rem;
          font-weight: 800;
          margin: 3rem 0 1.5rem;
          color: #26313B;
          border-bottom: 2px solid rgba(217,163,46,.45);
          padding-bottom: .5rem;
        }

        .md-h3 {
          font-size: 1.6rem;
          font-weight: 700;
          margin: 2.5rem 0 1.25rem;
          color: #26313B;
        }

        .md-h4 {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 2rem 0 1rem;
          color: #26313B;
        }

        .md-p {
          margin-bottom: 1.75rem;
        }

        .inline-code {
          background: #F1EEE5;
          padding: .2rem .45rem;
          border-radius: .35rem;
          font-family: "JetBrains Mono", monospace;
          color: #A8720E;
        }

        .code-block-wrapper {
          margin: 2.5rem 0;
          border-radius: .75rem;
          overflow: hidden;
          border: 1px solid #E1DCCE;
          background: #F1EEE5;
        }

        .code-block-header {
          display: flex;
          justify-content: space-between;
          padding: .6rem 1rem;
          background: rgba(168,114,14,.10);
          font-size: .75rem;
          text-transform: uppercase;
        }

        .copy-code-button {
          cursor: pointer;
          background: rgba(232,180,76,.18);
          border: 1px solid #D9A32E;
          padding: .25rem .6rem;
          border-radius: .4rem;
          color: #A8720E;
          font-weight: 600;
        }

        .markdown-blockquote {
          margin: 2.5rem 0;
          padding: 1.25rem 1.5rem;
          background: #F1EEE5;
          border-left: 4px solid #E8B44C;
          border-radius: 0 .5rem .5rem 0;
        }

        .markdown-image {
          max-width: 100%;
          border-radius: .75rem;
          margin: 3rem 0;
        }

        .youtube-embed-wrapper {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          margin: 3rem 0;
        }

        .youtube-embed-wrapper iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border-radius: .75rem;
        }

        /* Mobile optimizations for better readability */
        @media (max-width: 768px) {
          .markdown-content {
            font-size: 1.0625rem;
            line-height: 1.75;
            letter-spacing: 0.01em;
          }

          .md-h1 {
            font-size: 1.875rem;
            margin: 2rem 0 1.25rem;
            line-height: 1.2;
          }

          .md-h2 {
            font-size: 1.5rem;
            margin: 1.75rem 0 1rem;
            line-height: 1.3;
            padding-bottom: .4rem;
          }

          .md-h3 {
            font-size: 1.25rem;
            margin: 1.5rem 0 0.875rem;
            line-height: 1.4;
          }

          .md-h4 {
            font-size: 1.125rem;
            margin: 1.25rem 0 0.75rem;
            line-height: 1.4;
          }

          .md-p {
            margin-bottom: 1.25rem;
          }

          .inline-code {
            padding: .15rem .35rem;
            font-size: 0.9em;
            word-break: break-word;
          }

          .code-block-wrapper {
            margin: 1.5rem -1rem;
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .code-block-header {
            padding: .5rem 1rem;
            font-size: .7rem;
          }

          .copy-code-button {
            font-size: .7rem;
            padding: .2rem .5rem;
          }

          .markdown-blockquote {
            margin: 1.75rem -0.5rem;
            padding: 1rem 1.25rem;
            border-left-width: 3px;
            font-size: 0.975rem;
          }

          .markdown-image {
            margin: 2rem -1rem;
            border-radius: .5rem;
          }

          .youtube-embed-wrapper {
            margin: 2rem -1rem;
          }

          .youtube-embed-wrapper iframe {
            border-radius: .5rem;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          .markdown-content {
            font-size: 1.0rem;
          }

          .md-h1 {
            font-size: 1.75rem;
          }

          .md-h2 {
            font-size: 1.375rem;
          }

          .md-h3 {
            font-size: 1.1875rem;
          }
        }
      `}</style>
    </div>
  );
};

export default MarkdownRenderer;
